// QR Kiosk v2 — route layout (the v2 analog of MidasV3Layout.tsx).
// Owns the scoped root class (.qr-kiosk-v2-root p-machineid tier-{tier}), the
// side-effect token-sheet import, the root TooltipProvider, the chrome, the
// offline slot, the REAL access-denied state, the centered phone-first column,
// and the <Outlet/>.
//
// Phone-first DOCUMENT-scroll page (NOT the v3 height:100%/overflow:hidden app
// frame). Styling = inline styles + scoped CSS-var tokens + Radix only (no MUI,
// no Tailwind, no shared redesign infra).

// Side-effect import — registers the v2 token sheet (scoped to .qr-kiosk-v2-root).
// NOT in index.scss — mirrors midas-v3-tokens.scss in MidasV3Layout.tsx.
import "./kiosk-v2-tokens.css";

import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
// Import ONLY TooltipProvider (renders no DOM, so no Tailwind leaks). A visible
// kiosk tooltip in a later sub-phase must build a kiosk-styled wrapper on raw
// @radix-ui/react-tooltip — NOT this module's Tailwind-styled TooltipContent.
import { TooltipProvider } from "../../customer/midas/components/v2/ui/tooltip";

import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../shared/hooks/useBreakpoint";
import useTranslation from "../../shared/hooks/useTranslation";
import { setErrorHandlerCallbacks } from "../../core/errorHandler";
import { showAccessDenied, hideAccessDenied } from "../accessDeniedSlice";
import accountContext from "../../account/accountContext";
import { refreshAccount } from "../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../account/routes";

import { setTier } from "./kioskV2Slice";
import KChrome from "./chrome/KChrome";
import AccountSheet from "./chrome/AccountSheet";
import GuestPassBanner from "./guest/GuestPassBanner";
import { isGuestRole } from "./guest/guestSession";
import { KioskOverlayContext } from "./shared/kioskOverlayRoot";
import OfflineStrip from "./shared/OfflineStrip";
import { useOfflineListener } from "./shared/useOfflineListener";
import { Glyph, GD } from "./shared/glyphs";

// Real access-denied state — fixes v1's perpetual spinner on 403 (ticket §5.1).
// NO auto-hide timer, NO toast, NO Loader. Chrome stays rendered above it.
const AccessDeniedState: React.FC<{ onCta: () => void }> = ({ onCta }) => {
  const { translate } = useTranslation("public");
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div
        style={{
          width: "100%",
          border: "1px dashed var(--border)",
          borderRadius: 14,
          background: "var(--card)",
          padding: "30px 22px",
          textAlign: "center",
        }}
      >
        <div style={{ color: "var(--muted)", display: "flex", justifyContent: "center" }}>
          <Glyph d={GD.lock} size={28} />
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--ink)", marginTop: 11 }}>
          {translate("kioskV2AccessDeniedTitle")}
        </div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 5, lineHeight: 1.5 }}>
          {translate("kioskV2AccessDeniedHint")}
        </div>
        <button
          type="button"
          onClick={onCta}
          style={{
            marginTop: 16,
            height: 48,
            width: "100%",
            borderRadius: 11,
            border: "1px solid var(--tier-accent)",
            background: "var(--tier-accent)",
            color: "var(--tier-accent-fg)",
            fontFamily: "inherit",
            fontSize: 14.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {translate("kioskV2AccessDeniedCta")}
        </button>
      </div>
    </div>
  );
};

const KioskV2Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPhone, isTablet } = useBreakpoint();

  // Round-6 (§1.2): the overlay/portal root — every v2 overlay portals into this node so
  // it stays inside the token scope AND outside any animated page subtree. Callback-ref
  // as state so overlay consumers re-render once the node exists.
  const [overlayEl, setOverlayEl] = useState<HTMLElement | null>(null);

  const tier = useAppSelector((s) => s.kioskV2.tier); // "login" | "guest" (KV2-8)
  const offline = useAppSelector((s) => s.kioskV2.offline);
  const lastLoadedAt = useAppSelector((s) => s.kioskV2.lastLoadedAt);
  const isAccessDenied = useAppSelector((s) => s.accessDenied.isAccessDenied);
  const userRole = useAppSelector((s) => s.account.user?.role);

  // KV2-8: drive the tier chrome (blue guest accents / re-mint countdown) off the
  // signed-in principal — a Guest JWT → tier "guest", any company role → "login".
  useEffect(() => {
    dispatch(setTier(isGuestRole(userRole) ? "guest" : "login"));
  }, [userRole, dispatch]);

  // KV2-5 (GAP-0): the ONLY writer of state.kioskV2.offline — seeds from navigator.onLine
  // and flips on window online/offline. Called once here, never per page.
  useOfflineListener();

  // Register the 403 hook exactly as v1's AccessDeniedNotification does. v1 and v2
  // trees never mount simultaneously, so the global callback cannot collide.
  useEffect(() => {
    setErrorHandlerCallbacks({ onAccessDenied: () => dispatch(showAccessDenied()) });
    return () => {
      dispatch(hideAccessDenied());
    };
  }, [dispatch]);

  // Clear on navigation — a 403 on one surface must not poison the next route.
  useEffect(() => {
    dispatch(hideAccessDenied());
  }, [location.pathname, dispatch]);

  const timestamps = Object.values(lastLoadedAt).filter((t): t is number => typeof t === "number");
  const cachedAt = timestamps.length ? Math.max(...timestamps) : undefined;

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;

  // Access-denied CTA = v1 logout semantics verbatim ("sign in with a different account").
  const handleAccessDeniedCta = () => {
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  return (
    <TooltipProvider delayDuration={200}>
      <KioskOverlayContext.Provider value={overlayEl}>
        {/* Round-6 RULE: this root MUST NEVER receive an animation/transform/filter class —
            it is the fixed-positioning containing-block anchor for every portaled overlay. */}
        <div
          ref={setOverlayEl}
          className={`qr-kiosk-v2-root p-machineid tier-${tier}`}
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg)",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: columnMaxWidth,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: "inherit",
            }}
          >
            <KChrome />
            <GuestPassBanner />
            {offline && <OfflineStrip cachedAt={cachedAt} />}
            {isAccessDenied ? <AccessDeniedState onCta={handleAccessDeniedCta} /> : <Outlet />}
            <AccountSheet />
          </div>
        </div>
      </KioskOverlayContext.Provider>
    </TooltipProvider>
  );
};

export default KioskV2Layout;
